import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { API_URL } from '../lib/utils';
import { Card } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { TrendingUp, TrendingDown, CalendarDays } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { FeeStructureTab } from '../components/finance/FeeStructureTab';
import { ProfitabilityTab } from '../components/finance/ProfitabilityTab';
import { CashFlowTab } from '../components/finance/CashFlowTab';
import { LaborCostTab } from '../components/finance/LaborCostTab';

const formatCurrency = (amount) => {
  return `INR ${new Intl.NumberFormat('en-IN', {
    maximumFractionDigits: 0
  }).format(amount)}`;
};

export const FinancePage = () => {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [timeLogs, setTimeLogs] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [feeStructure, setFeeStructure] = useState([]);
  const [teamSalaries, setTeamSalaries] = useState([]);
  const [cashflowExpenses, setCashflowExpenses] = useState([]);
  const [reimbursements, setReimbursements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cashflowPeriod, setCashflowPeriod] = useState(0);
  const [statsPeriod, setStatsPeriod] = useState('lifetime');

  useEffect(() => {
    // Admin, manager, finance can see all finance. Team leads can see finance for projects they lead.
    if (user?.role && !['admin', 'manager', 'finance', 'team_lead'].includes(user.role)) {
      setLoading(false);
      return;
    }
    fetchData();
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    try {
      const [projectsRes, logsRes, teamRes, feeRes, salaryRes, cashflowRes, reimbursementsRes] = await Promise.all([
        axios.get(`${API_URL}/projects`, { withCredentials: true }),
        axios.get(`${API_URL}/time-logs`, { withCredentials: true }),
        axios.get(`${API_URL}/team`, { withCredentials: true }),
        axios.get(`${API_URL}/fee-structure`, { withCredentials: true }),
        axios.get(`${API_URL}/team-salaries`, { withCredentials: true }),
        axios.get(`${API_URL}/cashflow-expenses`, { withCredentials: true }),
        axios.get(`${API_URL}/reimbursements`, { withCredentials: true })
      ]);
      let projectsData = projectsRes.data;
      let feeData = feeRes.data;
      let logsData = logsRes.data;
      let cashflowData = cashflowRes.data;
      let reimbursementsData = reimbursementsRes.data.filter(r => r.status !== 'rejected');
      
      // Team leads only see finance for projects they lead
      if (user?.role === 'team_lead') {
        const ledProjectIds = new Set(projectsData.filter(p => p.project_lead === user.user_id).map(p => p.project_id));
        projectsData = projectsData.filter(p => ledProjectIds.has(p.project_id));
        feeData = feeData.filter(f => ledProjectIds.has(f.project_id));
        logsData = logsData.filter(l => ledProjectIds.has(l.project_id));
        cashflowData = []; // Team leads don't see org-level cash flow expenses
        reimbursementsData = reimbursementsData.filter(r => ledProjectIds.has(r.project_id));
      }
      
      setProjects(projectsData);
      setTimeLogs(logsData);
      setTeamMembers(teamRes.data);
      setFeeStructure(feeData);
      setTeamSalaries(salaryRes.data);
      setCashflowExpenses(cashflowData);
      setReimbursements(reimbursementsData);
    } catch (error) {
      toast.error('Failed to load finance data');
    } finally {
      setLoading(false);
    }
  };

  // Project profitability calculations
  const projectProfitability = useMemo(() => {
    return projects.map(project => {
      const projectFees = feeStructure.filter(f => f.project_id === project.project_id);
      const projectLogs = timeLogs.filter(log => log.project_id === project.project_id);

      const paidIncome = projectFees.filter(f => f.payment_status === 'received').reduce((sum, f) => sum + f.amount, 0);
      const pendingIncome = projectFees.filter(f => f.invoice_status === 'invoiced' && f.payment_status === 'pending').reduce((sum, f) => sum + f.amount, 0);
      const totalProjectValue = projectFees.reduce((sum, f) => sum + f.amount, 0);

      let laborCost = 0;
      const billableHoursByUser = {};
      projectLogs.filter(log => log.billable).forEach(log => {
        if (!billableHoursByUser[log.user_id]) billableHoursByUser[log.user_id] = 0;
        billableHoursByUser[log.user_id] += log.duration_minutes / 60;
      });
      Object.entries(billableHoursByUser).forEach(([userId, hours]) => {
        const salary = teamSalaries.find(s => s.user_id === userId);
        if (salary) laborCost += hours * salary.hourly_rate;
      });

      const projectReimbursements = reimbursements
        .filter(r => r.project_id === project.project_id && r.status !== 'rejected')
        .reduce((sum, r) => sum + r.amount, 0);

      const totalBillableHours = Object.values(billableHoursByUser).reduce((sum, h) => sum + h, 0);
      const totalExpenses = laborCost + projectReimbursements;

      return {
        ...project,
        totalProjectValue, paidIncome, pendingIncome, laborCost,
        reimbursementExpenses: projectReimbursements,
        totalExpenses, totalBillableHours,
        profit: paidIncome - totalExpenses
      };
    });
  }, [projects, feeStructure, timeLogs, teamSalaries, reimbursements]);

  // Cash flow months
  const getCashFlowMonths = useMemo(() => {
    const months = [];
    const now = new Date();
    const startOffset = cashflowPeriod * 6;
    for (let i = startOffset; i < startOffset + 6; i++) {
      const monthDate = new Date(now.getFullYear(), now.getMonth() + i, 1);
      months.push({
        key: monthDate.toISOString().slice(0, 7),
        name: monthDate.toLocaleString('default', { month: 'short', year: 'numeric' })
      });
    }
    return months;
  }, [cashflowPeriod]);

  // Cash flow data
  const cashFlowData = useMemo(() => {
    const months = getCashFlowMonths;
    const incomeByMonth = {};
    const expensesByMonth = {};
    const reimbursementsByMonth = {};
    months.forEach(m => { incomeByMonth[m.key] = 0; expensesByMonth[m.key] = 0; reimbursementsByMonth[m.key] = 0; });

    feeStructure.forEach(item => {
      // Exclude voided payments and cancelled invoices from cash flow income
      if (item.payment_status === 'void' || item.invoice_status === 'cancelled') return;
      if (item.tentative_billing_date) {
        const billingMonth = item.tentative_billing_date.slice(0, 7);
        if (incomeByMonth[billingMonth] !== undefined) {
          incomeByMonth[billingMonth] += item.amount;
        }
      }
    });

    cashflowExpenses.forEach(exp => {
      if (expensesByMonth[exp.month_year] !== undefined) expensesByMonth[exp.month_year] += exp.amount;
    });

    reimbursements.forEach(r => {
      if (r.date && r.status !== 'rejected') {
        const reimbMonth = r.date.slice(0, 7);
        if (reimbursementsByMonth[reimbMonth] !== undefined) reimbursementsByMonth[reimbMonth] += r.amount;
        if (expensesByMonth[reimbMonth] !== undefined) expensesByMonth[reimbMonth] += r.amount;
      }
    });

    // Group expenses by expense_head (parent) → each parent contains its sub_heads.
    // Parent row shows monthly totals summed across all sub-heads.
    const expenseGroupsByHead = {};
    cashflowExpenses.forEach(exp => {
      const head = exp.expense_head;
      const subHead = exp.sub_head || '';
      if (!expenseGroupsByHead[head]) {
        const emptyByMonth = {};
        months.forEach(m => { emptyByMonth[m.key] = { amount: 0 }; });
        expenseGroupsByHead[head] = {
          head,
          isReimbursement: false,
          byMonth: emptyByMonth,       // summed across sub-heads
          subGroups: {}                 // keyed by subHead label
        };
      }
      const parent = expenseGroupsByHead[head];

      // Track/create sub-head bucket
      if (!parent.subGroups[subHead]) {
        const subByMonth = {};
        months.forEach(m => { subByMonth[m.key] = { amount: 0, expenseId: null }; });
        parent.subGroups[subHead] = { subHead, byMonth: subByMonth };
      }
      const sub = parent.subGroups[subHead];

      if (sub.byMonth[exp.month_year]) {
        // If multiple expenses share head+subHead+month, sum them and keep last id for edit target
        sub.byMonth[exp.month_year] = {
          amount: (sub.byMonth[exp.month_year].amount || 0) + exp.amount,
          expenseId: exp.expense_id
        };
      }
      if (parent.byMonth[exp.month_year]) {
        parent.byMonth[exp.month_year] = {
          amount: (parent.byMonth[exp.month_year].amount || 0) + exp.amount
        };
      }
    });

    // Convert sub-heads to array (sorted for stable UI)
    const expenseGroups = Object.values(expenseGroupsByHead).map(g => ({
      ...g,
      subGroups: Object.values(g.subGroups).sort((a, b) => (a.subHead || '').localeCompare(b.subHead || ''))
    }));

    if (Object.values(reimbursementsByMonth).some(v => v > 0)) {
      const reimbByMonth = {};
      months.forEach(m => { reimbByMonth[m.key] = { amount: reimbursementsByMonth[m.key] }; });
      expenseGroups.push({
        head: 'Reimbursements',
        isReimbursement: true,
        byMonth: reimbByMonth,
        subGroups: [{ subHead: 'Team Expenses', byMonth: (() => {
          const inner = {};
          months.forEach(m => { inner[m.key] = { amount: reimbursementsByMonth[m.key], expenseId: null }; });
          return inner;
        })() }]
      });
    }

    return {
      months, incomeByMonth, expensesByMonth, reimbursementsByMonth,
      expenseGroups,
      netByMonth: months.map(m => ({ month: m.key, income: incomeByMonth[m.key], expenses: expensesByMonth[m.key], net: incomeByMonth[m.key] - expensesByMonth[m.key] }))
    };
  }, [getCashFlowMonths, feeStructure, cashflowExpenses, reimbursements]);

  // Labor costs per project with user breakdown
  const laborCostsByProject = useMemo(() => {
    return projects.map(project => {
      const projectLogs = timeLogs.filter(log => log.project_id === project.project_id);
      const userBreakdown = {};
      projectLogs.forEach(log => {
        if (!userBreakdown[log.user_id]) {
          const member = teamMembers.find(m => m.user_id === log.user_id);
          const salary = teamSalaries.find(s => s.user_id === log.user_id);
          userBreakdown[log.user_id] = { name: member?.name || log.user_name || 'Unknown', billableHours: 0, hourlyRate: salary?.hourly_rate || 0, cost: 0 };
        }
        if (log.billable) {
          const hours = log.duration_minutes / 60;
          userBreakdown[log.user_id].billableHours += hours;
          userBreakdown[log.user_id].cost = userBreakdown[log.user_id].billableHours * userBreakdown[log.user_id].hourlyRate;
        }
      });
      return {
        ...project,
        userBreakdown: Object.values(userBreakdown),
        totalBillableHours: Object.values(userBreakdown).reduce((sum, u) => sum + (u.billableHours || 0), 0),
        totalLaborCost: Object.values(userBreakdown).reduce((sum, u) => sum + u.cost, 0)
      };
    });
  }, [projects, timeLogs, teamMembers, teamSalaries]);

  // Summary values with period filter
  const periodFilteredFees = useMemo(() => {
    if (statsPeriod === 'lifetime') return feeStructure;
    const now = new Date();
    let startDate;
    switch (statsPeriod) {
      case 'month': startDate = new Date(now.getFullYear(), now.getMonth(), 1); break;
      case 'quarter': startDate = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1); break;
      case 'half_year': startDate = new Date(now.getFullYear(), now.getMonth() >= 6 ? 6 : 0, 1); break;
      case 'year': startDate = new Date(now.getFullYear(), 0, 1); break;
      case 'prev_year': startDate = new Date(now.getFullYear() - 1, 0, 1); break;
      default: return feeStructure;
    }
    const endDate = statsPeriod === 'prev_year' ? new Date(now.getFullYear(), 0, 1) : now;
    return feeStructure.filter(f => {
      if (!f.tentative_billing_date) return statsPeriod === 'lifetime';
      const d = new Date(f.tentative_billing_date);
      return d >= startDate && d < endDate;
    });
  }, [feeStructure, statsPeriod]);

  const totalPaidIncome = periodFilteredFees.filter(f => f.payment_status === 'received').reduce((sum, f) => sum + f.amount, 0);
  const totalPendingIncome = periodFilteredFees.filter(f => f.payment_status === 'pending').reduce((sum, f) => sum + f.amount, 0);
  const totalLaborCost = projectProfitability.reduce((sum, p) => sum + p.laborCost, 0);
  const totalProfit = totalPaidIncome - totalLaborCost;

  const handleDeleteCashflowExpense = async (expenseId) => {
    if (!window.confirm('Delete this expense?')) return;
    try {
      await axios.delete(`${API_URL}/cashflow-expenses/${expenseId}`, { withCredentials: true });
      toast.success('Expense deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete expense');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>;
  }

  if (!user || !['admin', 'manager', 'finance', 'team_lead'].includes(user.role)) {
    return (
      <Card className="p-12 text-center">
        <h3 className="text-xl font-heading font-semibold mb-2">Access Restricted</h3>
        <p className="text-muted-foreground">This section is only available to authorized roles</p>
      </Card>
    );
  }

  const isTeamLead = user?.role === 'team_lead';
  const isFullFinanceAccess = ['admin', 'manager'].includes(user?.role);

  return (
    <div data-testid="finance-page" className="pt-12">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-4xl font-heading font-bold tracking-tight mb-2">Finance</h1>
          <p className="text-base text-muted-foreground">{isTeamLead ? 'Fee structure for your projects' : 'Manage fee structures, cash flow, and labor costs'}</p>
        </div>
      </div>

      {/* Summary Cards - only for admin/manager */}
      {isFullFinanceAccess && (
      <>
      {/* Period Filter + Summary Cards */}
      <div className="flex items-center justify-between mb-4">
        <span className="text-sm text-muted-foreground flex items-center gap-2">
          <CalendarDays className="h-4 w-4" /> Statistics Period
        </span>
        <Select value={statsPeriod} onValueChange={setStatsPeriod}>
          <SelectTrigger className="w-48" data-testid="finance-period-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="month">This Month</SelectItem>
            <SelectItem value="quarter">This Quarter</SelectItem>
            <SelectItem value="half_year">Half Year</SelectItem>
            <SelectItem value="year">This Year</SelectItem>
            <SelectItem value="prev_year">Previous Year</SelectItem>
            <SelectItem value="lifetime">Lifetime</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Card className="p-4">
          <p className="text-sm text-muted-foreground mb-1">Total Received</p>
          <p className="text-2xl font-heading font-bold text-green-600">{formatCurrency(totalPaidIncome)}</p>
        </Card>
        <Card className="p-4">
          <p className="text-sm text-muted-foreground mb-1">Pending Income</p>
          <p className="text-2xl font-heading font-bold text-amber-600">{formatCurrency(totalPendingIncome)}</p>
        </Card>
        <Card className="p-4">
          <p className="text-sm text-muted-foreground mb-1">Total Labor Cost</p>
          <p className="text-2xl font-heading font-bold text-orange-600">{formatCurrency(totalLaborCost)}</p>
        </Card>
        <Card className="p-4">
          <p className="text-sm text-muted-foreground mb-1">Net Profit</p>
          <div className="flex items-center gap-2">
            <p className={`text-2xl font-heading font-bold ${totalProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {formatCurrency(Math.abs(totalProfit))}
            </p>
            {totalProfit >= 0 ? <TrendingUp className="h-5 w-5 text-green-600" /> : <TrendingDown className="h-5 w-5 text-red-600" />}
          </div>
        </Card>
      </div>
      </>
      )}

      {isTeamLead ? (
        /* Team Lead: Only Fee Structure */
        <FeeStructureTab projects={projects} feeStructure={feeStructure} formatCurrency={formatCurrency} onRefresh={fetchData} />
      ) : (
      <Tabs defaultValue="fee-structure" className="space-y-6">
        <TabsList className={`grid w-full max-w-xl ${user?.role === 'admin' ? 'grid-cols-4' : 'grid-cols-3'}`}>
          <TabsTrigger value="fee-structure">Fee Structure</TabsTrigger>
          <TabsTrigger value="profitability">Profitability</TabsTrigger>
          <TabsTrigger value="cashflow">Cash Flow</TabsTrigger>
          {user?.role === 'admin' && <TabsTrigger value="labor">Labor Costs</TabsTrigger>}
        </TabsList>

        <TabsContent value="fee-structure" className="space-y-4">
          <FeeStructureTab projects={projects} feeStructure={feeStructure} formatCurrency={formatCurrency} onRefresh={fetchData} />
        </TabsContent>

        <TabsContent value="profitability" className="space-y-4">
          <ProfitabilityTab projectProfitability={projectProfitability} formatCurrency={formatCurrency} />
        </TabsContent>

        <TabsContent value="cashflow" className="space-y-4">
          <CashFlowTab
            cashFlowData={cashFlowData}
            getCashFlowMonths={getCashFlowMonths}
            cashflowPeriod={cashflowPeriod}
            setCashflowPeriod={setCashflowPeriod}
            formatCurrency={formatCurrency}
            onRefresh={fetchData}
            onDeleteExpense={handleDeleteCashflowExpense}
            feeStructure={feeStructure}
            cashflowExpenses={cashflowExpenses}
            reimbursements={reimbursements}
            projects={projects}
          />
        </TabsContent>

        {user?.role === 'admin' && (
        <TabsContent value="labor" className="space-y-4">
          <LaborCostTab
            teamMembers={teamMembers}
            teamSalaries={teamSalaries}
            laborCostsByProject={laborCostsByProject}
            formatCurrency={formatCurrency}
            onRefresh={fetchData}
          />
        </TabsContent>
        )}
      </Tabs>
      )}
    </div>
  );
};
